/**
 * Reveal scene — show the unscrambled answer + first guesser headline.
 *
 * Lives between rounds for a few seconds. The cascade animation that lands
 * each remaining letter into its correct position is run on the ROUND scene
 * (via `round.cascadeReveal()`) BEFORE this scene is shown — main.ts holds
 * the round scene visible long enough for the cascade + an 800ms pause to
 * complete, then swaps to this scene for the headline.
 *
 * Visual: huge gold answer text (each letter as a green-glow tile), the
 * first guesser as the headline winner card, and a small chip below showing
 * the count of subsequent correct guessers and their cumulative points (when
 * any).
 */

import type { RevealState } from '../state/types.js';
import { createSparkles, type SparklesHandle } from '../ui/sparkles.js';

export interface RevealHandle {
  el: HTMLDivElement;
  setReveal(r: RevealState): void;
  setVisible(v: boolean): void;
}

const STYLES = `
  @keyframes an-reveal-pop {
    0%   { opacity: 0; transform: translateY(-12px) scale(0.96); }
    60%  { opacity: 1; transform: translateY(2px) scale(1.02); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes an-reveal-glow {
    0%, 100% { text-shadow: 0 8px 0 #03073D, 0 0 30px rgba(255,196,61,0.5); }
    50%      { text-shadow: 0 8px 0 #03073D, 0 0 90px rgba(255,196,61,1.0); }
  }
  .an-reveal-wrap {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 80px;
    text-align: center;
    z-index: 30;
    color: #fff;
    font-family: 'Oswald','Bebas Neue',Impact,sans-serif;
  }
  .an-reveal-eyebrow {
    font-size: 28px;
    letter-spacing: 0.32em;
    color: #94A3B8;
    text-transform: uppercase;
    animation: an-reveal-pop 480ms ease-out both;
  }
  .an-reveal-label {
    margin-top: 20px;
    font-size: 36px;
    letter-spacing: 0.34em;
    color: #E2E8F0;
    text-transform: uppercase;
    animation: an-reveal-pop 480ms ease-out 80ms both;
  }
  .an-reveal-answer {
    margin-top: 20px;
    display: flex;
    flex-wrap: wrap;
    gap: 14px 18px;
    align-items: center;
    justify-content: center;
    max-width: 1820px;
    animation: an-reveal-pop 540ms ease-out 160ms both;
  }
  .an-reveal-letter {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: var(--an-reveal-tile, 140px);
    height: var(--an-reveal-tile, 140px);
    border-radius: 16px;
    background: linear-gradient(180deg, #1B3F26 0%, #1F8A3D 55%, #155A28 100%);
    border: 4px solid var(--sg-primary, #FFC43D);
    color: #FFFFFF;
    font-family: 'Oswald','Bebas Neue',Impact,sans-serif;
    font-weight: 700;
    font-size: var(--an-reveal-font, 100px);
    line-height: 1;
    box-shadow:
      0 12px 0 #062E10,
      0 0 60px rgba(34,197,94,0.45),
      0 18px 30px rgba(0,0,0,0.55);
    text-shadow: 0 4px 0 #003913, 0 0 36px rgba(34,197,94,0.85);
    animation: an-reveal-glow 3.4s ease-in-out infinite;
  }
  .an-reveal-letter.is-space {
    background: transparent;
    border: none;
    box-shadow: none;
    width: calc(var(--an-reveal-tile, 140px) * 0.45);
  }
  .an-reveal-winner {
    margin-top: 56px;
    padding: 22px 56px;
    font-size: 56px;
    letter-spacing: 0.06em;
    color: #FFFFFF;
    background: linear-gradient(180deg, #1F8A3D 0%, #155A28 100%);
    border: 4px solid var(--sg-primary, #FFC43D);
    border-radius: 18px;
    box-shadow: 0 0 60px rgba(34,197,94,0.55), 0 12px 30px rgba(0,0,0,0.55);
    text-shadow: 0 4px 0 #003913;
    animation: an-reveal-pop 540ms ease-out 240ms both;
  }
  .an-reveal-winner.is-empty {
    background: linear-gradient(180deg, #3a1212 0%, #5b1818 100%);
    color: #FCA5A5;
    text-shadow: 0 4px 0 rgba(0,0,0,0.4);
  }
  .an-reveal-extras {
    margin-top: 18px;
    padding: 8px 22px;
    font-size: 22px;
    letter-spacing: 0.06em;
    color: #E2E8F0;
    background: rgba(6,12,233,0.45);
    border: 2px solid var(--sg-accent, #00BFA6);
    border-radius: 999px;
    box-shadow: 0 4px 14px rgba(0,191,166,0.25);
    animation: an-reveal-pop 480ms ease-out 320ms both;
  }
  .an-reveal-extras.hidden { display: none; }
`;

function ensureStyles(): void {
  if (document.getElementById('an-reveal-style')) return;
  const s = document.createElement('style');
  s.id = 'an-reveal-style';
  s.textContent = STYLES;
  document.head.appendChild(s);
}

interface ReadProfile { tile: number; font: number }

// Reveal scene uses the FULL stage (no side leaderboard) — wider budget.
function pickProfile(visibleLen: number): ReadProfile {
  if (visibleLen <= 4) return { tile: 240, font: 176 };
  if (visibleLen <= 6) return { tile: 210, font: 152 };
  if (visibleLen <= 8) return { tile: 184, font: 132 };
  if (visibleLen <= 10) return { tile: 156, font: 112 };
  return { tile: 128, font: 92 };
}

export function createReveal(stage: HTMLElement): RevealHandle {
  ensureStyles();
  const el = document.createElement('div');
  el.className = 'an-reveal-wrap';
  el.setAttribute('data-scene', 'reveal');
  el.setAttribute('data-testid', 'an-reveal');
  el.innerHTML = `
    <div class="an-reveal-eyebrow" data-testid="an-reveal-eyebrow"></div>
    <div class="an-reveal-label">The word was</div>
    <div class="an-reveal-answer" data-testid="an-reveal-answer" data-role="answer"></div>
    <div class="an-reveal-winner" data-testid="an-reveal-winner" data-role="winner"></div>
    <div class="an-reveal-extras hidden" data-testid="an-reveal-extras" data-role="extras"></div>
  `;
  stage.appendChild(el);

  const eyebrow = el.querySelector<HTMLDivElement>('[data-testid="an-reveal-eyebrow"]')!;
  const answerEl = el.querySelector<HTMLDivElement>('[data-role="answer"]')!;
  const winnerEl = el.querySelector<HTMLDivElement>('[data-role="winner"]')!;
  const extrasEl = el.querySelector<HTMLDivElement>('[data-role="extras"]')!;
  const sparkles: SparklesHandle = createSparkles(el);

  let lastWordId = '';
  let lastShown = false;

  function paintAnswerTiles(answer: string): void {
    answerEl.innerHTML = '';
    const upper = answer.toUpperCase();
    const visibleLen = upper.replace(/\s/g, '').length;
    const profile = pickProfile(visibleLen);
    answerEl.style.setProperty('--an-reveal-tile', `${profile.tile}px`);
    answerEl.style.setProperty('--an-reveal-font', `${profile.font}px`);
    for (const ch of upper) {
      const span = document.createElement('span');
      span.className = ch === ' ' ? 'an-reveal-letter is-space' : 'an-reveal-letter';
      span.textContent = ch === ' ' ? '' : ch;
      answerEl.appendChild(span);
    }
  }

  return {
    el,
    setReveal(r) {
      eyebrow.textContent = r.category ? r.category.toUpperCase() : '';
      if (r.wordId !== lastWordId) {
        lastWordId = r.wordId;
        paintAnswerTiles(r.answer);
      }
      if (r.firstCorrect) {
        const seconds = (r.firstCorrect.elapsedMs / 1000).toFixed(1);
        winnerEl.textContent = `${r.firstCorrect.displayName} \u00B7 +${r.firstCorrect.points} pts \u00B7 ${seconds}s`;
        winnerEl.classList.remove('is-empty');
      } else {
        winnerEl.textContent = 'NOBODY GOT IT';
        winnerEl.classList.add('is-empty');
      }
      // Subsequent correct guessers chip (only when 2+ scored).
      const otherCount = Math.max(0, r.corrects.length - 1);
      if (otherCount > 0) {
        const otherPts = r.corrects.slice(1).reduce((sum, c) => sum + c.points, 0);
        extrasEl.textContent = `+${otherCount} more correct \u00B7 ${otherPts.toLocaleString()} pts shared`;
        extrasEl.classList.remove('hidden');
      } else {
        extrasEl.classList.add('hidden');
      }
    },
    setVisible(v) {
      el.style.display = v ? 'flex' : 'none';
      if (v && !lastShown) {
        lastShown = true;
      }
      if (v) {
        // Burst sparkles into the answer area on each reveal.
        const cy = 1080 / 2 - 60;
        sparkles.burst({ x: 1920 / 2, y: cy, count: 70, speed: 1.4 });
      } else {
        lastShown = false;
      }
    },
  };
}
